'use client'

import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  MapPin,
  Clock,
  CreditCard,
  Banknote,
  Smartphone,
  Plus,
  Check,
  ChevronRight,
  ShoppingBag,
  Lock,
} from 'lucide-react'
import { useCartStore } from '@/stores/cart'
import { useAuthStore } from '@/stores/auth'
import { useCheckoutStore, generateDeliverySlots } from '@/stores/checkout'
import { useOrderStore } from '@/stores/order'
import { useCurrencyStore } from '@/stores/currency'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'
import ECommerceHeader from '@/components/layout/ECommerceHeader'
import Footer from '@/components/layout/Footer'
import type { Address } from '@/types/user'
import { faker } from '@faker-js/faker'

const PAYMENT_METHODS = [
  { id: 'cod', name: 'Cash on Delivery', icon: Banknote, description: 'Pay when you receive' },
  { id: 'bkash', name: 'bKash', icon: Smartphone, description: 'Mobile payment' },
  { id: 'nagad', name: 'Nagad', icon: Smartphone, description: 'Mobile payment' },
  { id: 'card', name: 'Credit/Debit Card', icon: CreditCard, description: 'Visa, Mastercard, Amex' },
]

export default function CheckoutPage() {
  const navigate = useNavigate()
  const { items, total, discount, coupon_code, clearCart } = useCartStore()
  const { isAuthenticated, addresses, addAddress } = useAuthStore()
  const {
    selectedAddress,
    selectedSlot,
    paymentMethod,
    guestName,
    guestEmail,
    guestPhone,
    orderNotes,
    setSelectedAddress,
    setSelectedSlot,
    setPaymentMethod,
    setGuestInfo,
    setOrderNotes,
    reset: resetCheckout,
  } = useCheckoutStore()
  const addOrder = useOrderStore((state) => state.addOrder)
  const getSymbol = useCurrencyStore((state) => state.getSymbol)
  const convert = useCurrencyStore((state) => state.convert)

  const [currentStep, setCurrentStep] = useState(1)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showAddressForm, setShowAddressForm] = useState(false)
  const [newAddress, setNewAddress] = useState({
    label: '',
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    area: '',
    postal_code: '',
  })

  const deliverySlots = generateDeliverySlots()
  const shippingCost = total >= 500 ? 0 : 60
  const finalTotal = total - discount + shippingCost

  // Group slots by date
  const slotsByDate = deliverySlots.reduce(
    (acc, slot) => {
      if (!acc[slot.date]) acc[slot.date] = []
      acc[slot.date].push(slot)
      return acc
    },
    {} as Record<string, typeof deliverySlots>
  )

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  const handleAddNewAddress = () => {
    const address: Address = {
      id: faker.string.uuid(),
      label: newAddress.label || 'Home',
      full_name: newAddress.full_name,
      phone: newAddress.phone,
      address_line1: newAddress.address_line1,
      address_line2: newAddress.address_line2,
      city: newAddress.city,
      area: newAddress.area,
      postal_code: newAddress.postal_code,
      is_default: addresses.length === 0,
      created_at: new Date().toISOString(),
    }
    addAddress(address)
    setSelectedAddress(address)
    setShowAddressForm(false)
    setNewAddress({
      label: '',
      full_name: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      area: '',
      postal_code: '',
    })
  }

  const canProceedStep1 = isAuthenticated ? selectedAddress : guestName && guestEmail && guestPhone
  const canProceedStep2 = selectedSlot
  const canProceedStep3 = paymentMethod

  const handlePlaceOrder = async () => {
    setIsProcessing(true)

    // Simulate order processing
    await new Promise((resolve) => setTimeout(resolve, 1500))

    const deliveryAddress: Address = selectedAddress || {
      id: faker.string.uuid(),
      label: 'Guest Address',
      full_name: guestName,
      phone: guestPhone,
      address_line1: 'Guest checkout',
      city: 'Dhaka',
      area: 'N/A',
      postal_code: '1000',
      is_default: false,
      created_at: new Date().toISOString(),
    }

    const order = addOrder({
      items: items.map((item) => ({
        id: faker.string.uuid(),
        product_id: item.product_id,
        title: item.title,
        image: item.image,
        price: item.price,
        original_price: item.original_price,
        quantity: item.quantity,
        subtotal: item.price * item.quantity,
        vendor_name: item.vendor_name,
      })),
      status: 'pending',
      timeline: [],
      subtotal: total,
      shipping_cost: shippingCost,
      discount,
      tax: 0,
      total: finalTotal,
      delivery_address: deliveryAddress,
      delivery_slot: selectedSlot!,
      payment_method: paymentMethod,
      payment_status: paymentMethod === 'cod' ? 'pending' : 'paid',
      coupon_code: coupon_code || undefined,
      notes: orderNotes || undefined,
    })

    // Clear cart and checkout state
    clearCart()
    resetCheckout()

    // Navigate to confirmation
    navigate(`/order-confirmation?order_number=${order.order_number}`)
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <ECommerceHeader />
        <main className="container mx-auto px-4 py-16 text-center">
          <ShoppingBag className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-4">Your cart is empty</h1>
          <p className="text-muted-foreground mb-8">Add some items before checking out</p>
          <Link to="/">
            <Button variant="primary">Start Shopping</Button>
          </Link>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <ECommerceHeader />

      <main className="container mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Checkout</h1>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  currentStep >= step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}
              >
                {currentStep > step ? <Check className="h-5 w-5" /> : step}
              </div>
              {step < 3 && (
                <div className={`w-16 md:w-24 h-1 ${currentStep > step ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Delivery Address */}
            {currentStep === 1 && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Delivery Address
                </h2>

                {isAuthenticated ? (
                  <>
                    {/* Saved Addresses */}
                    {addresses.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {addresses.map((address) => (
                          <div
                            key={address.id}
                            onClick={() => setSelectedAddress(address)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                              selectedAddress?.id === address.id
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="font-medium">{address.label}</span>
                                {address.is_default && (
                                  <span className="ml-2 text-xs bg-muted px-2 py-0.5 rounded">Default</span>
                                )}
                              </div>
                              {selectedAddress?.id === address.id && (
                                <Check className="h-5 w-5 text-primary" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{address.full_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {address.address_line1}
                              {address.address_line2 && `, ${address.address_line2}`}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {address.area}, {address.city} - {address.postal_code}
                            </p>
                            <p className="text-sm text-muted-foreground">{address.phone}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New Address */}
                    {showAddressForm ? (
                      <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                        <div className="grid grid-cols-2 gap-4">
                          <Input
                            placeholder="Label (e.g., Home, Office)"
                            value={newAddress.label}
                            onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                          />
                          <Input
                            placeholder="Full Name"
                            value={newAddress.full_name}
                            onChange={(e) => setNewAddress({ ...newAddress, full_name: e.target.value })}
                          />
                        </div>
                        <Input
                          placeholder="Phone Number"
                          value={newAddress.phone}
                          onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                        />
                        <Input
                          placeholder="Address Line 1"
                          value={newAddress.address_line1}
                          onChange={(e) => setNewAddress({ ...newAddress, address_line1: e.target.value })}
                        />
                        <Input
                          placeholder="Address Line 2 (Optional)"
                          value={newAddress.address_line2}
                          onChange={(e) => setNewAddress({ ...newAddress, address_line2: e.target.value })}
                        />
                        <div className="grid grid-cols-3 gap-4">
                          <Input
                            placeholder="City"
                            value={newAddress.city}
                            onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                          />
                          <Input
                            placeholder="Area"
                            value={newAddress.area}
                            onChange={(e) => setNewAddress({ ...newAddress, area: e.target.value })}
                          />
                          <Input
                            placeholder="Postal Code"
                            value={newAddress.postal_code}
                            onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setShowAddressForm(false)}>
                            Cancel
                          </Button>
                          <Button variant="primary" onClick={handleAddNewAddress}>
                            Save Address
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button variant="outline" className="w-full" onClick={() => setShowAddressForm(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add New Address
                      </Button>
                    )}
                  </>
                ) : (
                  /* Guest Checkout Form */
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground mb-4">
                      <Link to="/login" className="text-primary hover:underline">
                        Sign in
                      </Link>{' '}
                      for faster checkout or continue as guest
                    </p>
                    <Input
                      placeholder="Full Name *"
                      value={guestName}
                      onChange={(e) => setGuestInfo(e.target.value, guestEmail, guestPhone)}
                    />
                    <Input
                      placeholder="Email Address *"
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestInfo(guestName, e.target.value, guestPhone)}
                    />
                    <Input
                      placeholder="Phone Number *"
                      value={guestPhone}
                      onChange={(e) => setGuestInfo(guestName, guestEmail, e.target.value)}
                    />
                  </div>
                )}

                <Button
                  variant="primary"
                  className="w-full mt-6"
                  onClick={() => setCurrentStep(2)}
                  disabled={!canProceedStep1}
                >
                  Continue to Delivery Time
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </Card>
            )}

            {/* Step 2: Delivery Time */}
            {currentStep === 2 && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Delivery Time
                </h2>

                <div className="space-y-4">
                  {Object.entries(slotsByDate)
                    .slice(0, 5)
                    .map(([date, slots]) => (
                      <div key={date}>
                        <h3 className="font-medium text-foreground mb-2">{formatDate(date)}</h3>
                        <div className="grid grid-cols-2 gap-2">
                          {slots.map((slot) => (
                            <button
                              key={slot.id}
                              onClick={() => slot.is_available && setSelectedSlot(slot)}
                              disabled={!slot.is_available}
                              className={`p-3 rounded-lg border text-sm transition-colors ${
                                selectedSlot?.id === slot.id
                                  ? 'border-primary bg-primary/5 text-primary'
                                  : slot.is_available
                                    ? 'border-border hover:border-primary/50'
                                    : 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                              }`}
                            >
                              {slot.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>

                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1"
                    onClick={() => setCurrentStep(3)}
                    disabled={!canProceedStep2}
                  >
                    Continue to Payment
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </Card>
            )}

            {/* Step 3: Payment */}
            {currentStep === 3 && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Payment Method
                </h2>

                <div className="space-y-3">
                  {PAYMENT_METHODS.map((method) => {
                    const Icon = method.icon
                    return (
                      <div
                        key={method.id}
                        onClick={() => setPaymentMethod(method.id)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                          paymentMethod === method.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-6 w-6 text-muted-foreground" />
                          <div className="flex-1">
                            <p className="font-medium">{method.name}</p>
                            <p className="text-sm text-muted-foreground">{method.description}</p>
                          </div>
                          {paymentMethod === method.id && <Check className="h-5 w-5 text-primary" />}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Order Notes */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Order Notes (Optional)
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground resize-none"
                    rows={3}
                    placeholder="Any special instructions for delivery?"
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setCurrentStep(2)}>
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1"
                    onClick={handlePlaceOrder}
                    disabled={!canProceedStep3 || isProcessing}
                  >
                    {isProcessing ? (
                      'Processing...'
                    ) : (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Place Order - {getSymbol()}
                        {convert(finalTotal).toLocaleString()}
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-foreground mb-4">Order Summary</h2>

              {/* Items Preview */}
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {items.map((item) => (
                  <div key={item.product_id} className="flex gap-3">
                    <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium line-clamp-2">{item.title}</p>
                      <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                      <p className="text-sm font-medium">
                        {getSymbol()}
                        {convert(item.price * item.quantity).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-2 pt-4 mt-4 border-t border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>
                    {getSymbol()}
                    {convert(total).toLocaleString()}
                  </span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-success">
                    <span>Discount</span>
                    <span>
                      -{getSymbol()}
                      {convert(discount).toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>{shippingCost === 0 ? 'Free' : `${getSymbol()}${convert(shippingCost)}`}</span>
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 mt-4 border-t border-border">
                <span className="font-semibold">Total</span>
                <span className="text-xl font-bold text-brand-orange">
                  {getSymbol()}
                  {convert(finalTotal).toLocaleString()}
                </span>
              </div>

              {/* Delivery Info */}
              {selectedAddress && (
                <div className="pt-4 mt-4 border-t border-border">
                  <p className="text-sm font-medium mb-1">Delivering to:</p>
                  <p className="text-sm text-muted-foreground">{selectedAddress.full_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedAddress.address_line1}</p>
                </div>
              )}

              {selectedSlot && (
                <div className="pt-4 mt-4 border-t border-border">
                  <p className="text-sm font-medium mb-1">Delivery time:</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(selectedSlot.date)} - {selectedSlot.label}
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
